import { useState } from 'react'
import { toast } from 'sonner'

import { Label } from '@/components/ui/label'
import { PasswordInput } from '@/domains/signup'

import { authClient } from './auth-client'

/** bare form (no card chrome) so it embeds inside the sign-in methods card.
 *  onDone fires after a successful change, onCancel collapses the editor. */
export function ChangePasswordForm({
  onDone,
  onCancel
}: {
  onDone?: () => void
  onCancel?: () => void
}) {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(event: React.SyntheticEvent) {
    event.preventDefault()
    setError('')

    if (next.length < 8) {
      setError('New password must be at least 8 characters.')
      return
    }
    if (next !== confirm) {
      setError('New passwords do not match.')
      return
    }

    setSubmitting(true)
    /** revokeOtherSessions signs out every other device on a password change,
     *  the standard response to a possible credential compromise. */
    const { error: authError } = await authClient.changePassword({
      currentPassword: current,
      newPassword: next,
      revokeOtherSessions: true
    })
    setSubmitting(false)

    if (authError) {
      setError(
        authError.message ??
          'Could not change your password. Check your current password and try again.'
      )
      return
    }

    setCurrent('')
    setNext('')
    setConfirm('')
    toast.success('Password updated')
    onDone?.()
  }

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='current-password'>Current password</Label>
        <PasswordInput
          id='current-password'
          value={current}
          onChange={setCurrent}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='new-password'>New password</Label>
        <PasswordInput
          id='new-password'
          placeholder='At least 8 characters'
          value={next}
          onChange={setNext}
        />
      </div>
      <div className='flex flex-col gap-1.5'>
        <Label htmlFor='confirm-password'>Confirm new password</Label>
        <PasswordInput
          id='confirm-password'
          value={confirm}
          onChange={setConfirm}
        />
      </div>

      {error && <p className='text-sm text-red-600'>{error}</p>}

      <div className='flex flex-col-reverse gap-2 sm:flex-row sm:justify-end'>
        {onCancel && (
          <button
            type='button'
            onClick={onCancel}
            className='w-full cursor-pointer rounded-xl px-4 py-2.5 text-[14px] font-medium text-ink-soft transition-colors hover:text-ink sm:w-auto'
          >
            Cancel
          </button>
        )}
        <button
          type='submit'
          disabled={
            current.length === 0 ||
            next.length === 0 ||
            confirm.length === 0 ||
            submitting
          }
          className='w-full cursor-pointer rounded-xl bg-accent px-6 py-2.5 text-[14px] font-medium text-white transition-all hover:-translate-y-px hover:bg-[#15296b] disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:bg-accent sm:w-auto'
        >
          {submitting ? 'Saving…' : 'Update password'}
        </button>
      </div>
    </form>
  )
}
